import { AxiosPromise, AxiosResponse, AxiosStatic } from "axios";
import {
  CommitmentInput,
  CommitmentPreviewPayload,
  Commitment,
  CommitmentWithError,
  GetCommitmentPreviewInput,
  Payment,
  PaymentInput,
  PaymentTimelineActivityEvent,
  GetCommitmentsInput,
  CommitmentPendingPayment,
  UpcomingPeriodRate,
} from "../types/Commitment";

export const getCommitmentUpcomingPeriodRate =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (
    commitmentId: string,
    periodId?: string
  ): AxiosPromise<UpcomingPeriodRate | null> =>
    await appAxios.get(
      `${serverURL}/commitments-v2/${commitmentId}/upcoming-period-rate`,
      {
        params: periodId ? { periodId } : undefined,
        withCredentials: true,
      }
    );

export const postCommitment =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (commitment: CommitmentInput): AxiosPromise<Commitment> =>
    await appAxios.post(`${serverURL}/commitments-v2`, commitment, {
      withCredentials: true,
    });

export const getCommitments =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (data?: GetCommitmentsInput): AxiosPromise<Commitment[]> => {
    const commitmentUrl = `${serverURL}/commitments-v2`;
    const dealCommitmentUrl = `${commitmentUrl}?dealId=${data?.dealId}`;
    const url = data?.dealId ? dealCommitmentUrl : commitmentUrl;
    return await appAxios.get(url, {
      withCredentials: true,
    });
  };

export const getPaymentHistories =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (paymentId: string): AxiosPromise<PaymentTimelineActivityEvent[]> =>
    await appAxios.get(
      `${serverURL}/commitments-v2/payments/${paymentId}/timeline`,
      {
        withCredentials: true,
      }
    );

/**
 * Retrieves pending payments for commitments.
 *
 * Business Logic:
 * 1. Returns the most overdue one-off payment, if any for a given commitment.
 * 2. Only includes payments where the underlying deal has either status: LIVE_ALL or CLOSED.
 * 3. Payments are sorted by due date in ascending order.
 * @returns An array of CommitmentPendingPayment objects.
 */
export const getPendingPayments =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (): AxiosPromise<CommitmentPendingPayment[]> =>
    await appAxios.get(`${serverURL}/commitments-v2/pending-payments`, {
      withCredentials: true,
    });

export const postPayment =
  (serverURL: string, appAxios: AxiosStatic) =>
  async ({
    commitmentId,
    paymentInput,
  }: {
    commitmentId: string;
    paymentInput: PaymentInput;
  }): AxiosPromise<Payment> =>
    await appAxios.post(
      `${serverURL}/commitments-v2/${commitmentId}/payments`,
      paymentInput,
      {
        withCredentials: true,
      }
    );

export const getCommitmentPreview =
  (serverURL: string, appAxios: AxiosStatic) =>
  async ({
    commitmentId,
    amount,
  }: GetCommitmentPreviewInput): Promise<CommitmentPreviewPayload> => {
    const previewUrl = `${serverURL}/commitments-v2/${commitmentId}/preview`;
    const url = `${previewUrl}?oneOffAmount=${amount}`;

    const response: AxiosResponse<CommitmentWithError> = await appAxios.get(
      url,
      {
        withCredentials: true,
      }
    );

    const { data } = response;

    return {
      commitmentId,
      amount,
      ...(data.error ? { errorMessage: data.error.message } : {}),
    };
  };
